/**
 * Name Selector – two boxes and arrows to pick who gets the invitation.
 *
 * Left box: people who are available to invite. Right box: people the admin has
 * chosen. Click a name to move it to the other box. The arrows do: move one
 * to the right, move one to the left, or move everyone to the right.
 */
import React from 'react';
import { Participant } from './types';
import './NameSelector.css';
import arrowIcon from '../../../../../../assests/icons/arrow.png';
import arrowsIcon from '../../../../../../assests/icons/arrows.png';

export interface NameSelectorProps {
  /** Available participants to select from */
  availableParticipants: Participant[];
  /** Selected participants */
  selectedParticipants: Participant[];
  /** Callback when moving one participant to right */
  onMoveRight: (participant: Participant) => void;
  /** Callback when moving one participant to left */
  onMoveLeft: (participant: Participant) => void;
  /** Callback when moving all participants to right */
  onMoveAll: () => void;
}

const NameSelector: React.FC<NameSelectorProps> = ({
  availableParticipants,
  selectedParticipants,
  onMoveRight,
  onMoveLeft,
  onMoveAll,
}) => {
  return (
    <div className="name-selector">
      {/* Left: people not yet selected. Click a name to add them to the right. */}
      <div className="name-selector__box name-selector__box--available">
        <div className="name-selector__box-content">
          {availableParticipants.length > 0 ? (
            availableParticipants.map((participant) => (
              <div
                key={participant.email}
                className="name-selector__name-item"
                onClick={() => onMoveRight(participant)}
              >
                <span className='name-selector__name'>{participant.name}</span>
                <span className='name-selector__email'>{participant.email}</span>
              </div>
            ))
          ) : (
            <div className="name-selector__empty">選択可能な参加者がありません</div>
          )}
        </div>
      </div>

      {/* Single right = move one from left to right. Single left = move one from right to left. Double right = move all to right. */}
      <div className="name-selector__arrows">
        <button
          className="name-selector__arrow-btn"
          onClick={() => {
            if (availableParticipants.length > 0) {
              onMoveRight(availableParticipants[0]);
            }
          }}
          disabled={availableParticipants.length === 0}
          aria-label="右へ移動"
        >
          <img src={arrowIcon} alt="" className="name-selector__arrow-icon" aria-hidden />
        </button>
        <button
          className="name-selector__arrow-btn"
          onClick={() => {
            if (selectedParticipants.length > 0) {
              onMoveLeft(selectedParticipants[0]);
            }
          }}
          disabled={selectedParticipants.length === 0}
          aria-label="左へ移動"
        >
          <img 
            src={arrowIcon} 
            alt="" 
            className="name-selector__arrow-icon name-selector__arrow-icon--left" 
            aria-hidden 
          />
        </button>
        <button
          className="name-selector__arrow-btn"
          onClick={onMoveAll}
          disabled={availableParticipants.length === 0}
          aria-label="すべて右へ移動"
        >
          <img src={arrowsIcon} alt="" className="name-selector__arrows-icon" aria-hidden />
        </button>
      </div>

      {/* Right: people who will receive the invitation. Click a name to remove them. */}
      <div className="name-selector__box name-selector__box--selected">
        <div className="name-selector__box-content">
          {selectedParticipants.length > 0 ? (
            selectedParticipants.map((participant) => (
              <div
                key={participant.email}
                className="name-selector__name-item"
                onClick={() => onMoveLeft(participant)}
              >
                <span className="name-selector__name">{participant.name}</span>
                <span className="name-selector__email">{participant.email}</span>
              </div>
            ))
          ) : (
            <div className="name-selector__empty">選択された参加者がありません</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NameSelector;
